'use client';

import React, { useState } from "react";
import { api, errText } from "./api";
import { AField, AInput, AError, ABtn, ALink, DevCodeHint, StepTag } from "./authUi";
import { SubscribeBox, usePricing } from "./Billing";
import { BUSINESS_HINTS } from "./businessHints";

const PLAN_LABELS = {
  "solo-monthly": "Solo · monthly", "solo-yearly": "Solo · yearly (2 months free)",
  "team-monthly": "Team · monthly", "team-yearly": "Team · yearly (2 months free)",
};

export const SignupFlow = ({ onAuthed, onLogin, plan = null }) => {
  const [step, setStep] = useState(1);
  // No plan-less trials (owner rule): if signup was reached without a plan
  // tag, the person picks one here first. The bill at trial end is exactly
  // what they signed up for.
  const [chosenPlan, setChosenPlan] = useState(plan);
  const [name, setName] = useState("");
  const [orgName, setOrgName] = useState("");
  const [businessText, setBusinessText] = useState("");
  const [hintsOpen, setHintsOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [devCode, setDevCode] = useState(null);
  const [code, setCode] = useState("");
  const [pin, setPin] = useState("");
  const [pin2, setPin2] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [authedData, setAuthedData] = useState(null);
  const [payChosen, setPayChosen] = useState(false);
  const [paid, setPaid] = useState(false);
  const pricing = usePricing();

  const start = async (e) => {
    e?.preventDefault();
    if (!name.trim() || !orgName.trim()) return setError("Fill in your name and business name.");
    if (!businessText.trim()) return setError("Tell us what your business is.");
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/signup/start", {
        name, org_name: orgName, business_text: businessText, phone,
        plan: chosenPlan || undefined,
      });
      setDevCode(data.dev_code || null);
      setStep(2);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/auth/signup/verify", { phone, code });
      setStep(3);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const complete = async (e) => {
    e.preventDefault();
    if (pin !== pin2) return setError("The two PINs don't match.");
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/signup/complete", { phone, pin });
      setAuthedData(data);
      setStep(4);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  // Step 0 — plan choice (only when signup was reached without a plan tag).
  if (!chosenPlan) {
    return (
      <div className="space-y-4">
        <div className="flex items-baseline justify-between">
          <h2 className="text-xl font-semibold tracking-tight text-zinc-900">First, pick your plan</h2>
        </div>
        <p className="text-sm text-zinc-600">
          14-day free trial on every plan — no card needed, cancel anytime. You only pay when your trial ends.
        </p>
        <div className="grid gap-2">
          {Object.entries(PLAN_LABELS).map(([key, label]) => (
            <button
              key={key}
              type="button"
              data-testid={`signup-pick-${key}`}
              onClick={() => setChosenPlan(key)}
              className="flex items-center justify-between rounded-xl border-2 border-zinc-200 bg-white px-4 py-3 text-left text-sm font-medium text-zinc-900 transition-colors hover:border-rose-500"
            >
              <span>{label}</span>
              <span className="text-zinc-500">
                {key === "solo-monthly" && "₹495/mo"}
                {key === "solo-yearly" && "₹4,950/yr"}
                {key === "team-monthly" && `₹${pricing?.team?.monthly ?? 975}/mo`}
                {key === "team-yearly" && `₹${(pricing?.team?.yearly ?? 9750).toLocaleString("en-IN")}/yr`}
              </span>
            </button>
          ))}
        </div>
        <ALink data-testid="signup-to-login" onClick={onLogin}>Already have an account? Log in</ALink>
      </div>
    );
  }

  if (step === 1) {
    const q = businessText.trim().toLowerCase();
    const matches = q.length >= 1
      ? BUSINESS_HINTS
          .filter((h) => h.toLowerCase().includes(q) && h.toLowerCase() !== q)
          .sort((a, b) => (a.toLowerCase().startsWith(q) ? 0 : 1) - (b.toLowerCase().startsWith(q) ? 0 : 1))
          .slice(0, 6)
      : [];
    const showHints = hintsOpen && matches.length > 0;
    return (
      <form onSubmit={start} className="space-y-4">
        <div className="flex items-baseline justify-between">
          <h2 className="text-xl font-semibold tracking-tight text-zinc-900">Create your account</h2>
          <StepTag step={1} total={3} />
        </div>
        {chosenPlan && PLAN_LABELS[chosenPlan] && (
          <div data-testid="signup-plan-chip" className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
            You're starting a <span className="font-semibold">{PLAN_LABELS[chosenPlan]}</span> free trial — 14 days, no card needed.
          </div>
        )}
        <AError>{error}</AError>
        <AField label="Your name">
          <AInput data-testid="signup-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Anita Rao" />
        </AField>
        <AField label="Business name">
          <AInput data-testid="signup-org-input" value={orgName} onChange={(e) => setOrgName(e.target.value)} placeholder="e.g. Rao Properties" />
        </AField>
        <AField label="What's your business?" hint="Type it in your own words — we'll set up a board that fits.">
          <div className="relative">
            <AInput
              data-testid="signup-business-input"
              value={businessText}
              onChange={(e) => { setBusinessText(e.target.value); setHintsOpen(true); }}
              onFocus={() => setHintsOpen(true)}
              onBlur={() => setTimeout(() => setHintsOpen(false), 150)}
              placeholder="e.g. Plywood dealer, Tuition centre, Caterer"
              autoComplete="off"
            />
            {showHints && (
              <div
                data-testid="business-hints"
                className="absolute z-20 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-zinc-200 bg-white shadow-lg"
              >
                {matches.map((h) => (
                  <button
                    key={h}
                    type="button"
                    data-testid={`business-hint-${h.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                    onMouseDown={(e) => { e.preventDefault(); setBusinessText(h); setHintsOpen(false); }}
                    className="block w-full px-3 py-2.5 text-left text-sm text-zinc-700 transition-colors hover:bg-zinc-50"
                  >
                    {h}
                  </button>
                ))}
              </div>
            )}
          </div>
        </AField>
        <AField label="Mobile number" hint="We'll send a one-time code by SMS to verify it.">
          <AInput data-testid="signup-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="98xxxxxxxx" inputMode="numeric" autoComplete="tel" />
        </AField>
        <ABtn data-testid="signup-start-button" type="submit" busy={busy}>Send me the code</ABtn>
        <div className="pt-1 text-center">
          <ALink data-testid="signup-login-link" onClick={onLogin}>Already have an account? Log in</ALink>
        </div>
      </form>
    );
  }

  if (step === 2) {
    return (
      <form onSubmit={verify} className="space-y-4">
        <div className="flex items-baseline justify-between">
          <h2 className="text-xl font-semibold tracking-tight text-zinc-900">Check your SMS</h2>
          <StepTag step={2} total={3} />
        </div>
        <p className="text-sm text-zinc-500">We sent a 6-digit code to <strong className="text-zinc-800">{phone}</strong>.</p>
        <DevCodeHint code={devCode} />
        <AError>{error}</AError>
        <AField label="6-digit code">
          <AInput
            data-testid="otp-input"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="123456"
            inputMode="numeric"
            className="text-center text-lg tracking-[0.5em]"
          />
        </AField>
        <ABtn data-testid="otp-verify-button" type="submit" busy={busy} disabled={code.length !== 6}>Verify</ABtn>
        <div className="flex items-center justify-between pt-1">
          <ALink data-testid="otp-back-link" onClick={() => { setError(""); setStep(1); }}>Back</ALink>
          <ALink data-testid="otp-resend-button" onClick={() => start()}>Resend code</ALink>
        </div>
      </form>
    );
  }

  if (step === 4) {
    const days = authedData?.billing?.days_left ?? 14;
    return (
      <div className="space-y-4" data-testid="signup-welcome">
        <div className="flex items-baseline justify-between">
          <h2 className="text-xl font-semibold tracking-tight text-zinc-900">You're all set! 🎉</h2>
        </div>
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          Your account for <strong>{orgName}</strong> is ready — with a board already set up for your{" "}
          <strong>{businessText}</strong>. Your <strong>{days}-day free trial</strong> has started — full access, no card needed.
        </div>
        <AError>{error}</AError>

        <ABtn data-testid="welcome-enter-button" onClick={() => onAuthed(authedData)} busy={busy && !payChosen}>
          Take me in →
        </ABtn>

        {!payChosen && !paid && (
          <div className="pt-1 text-center">
            <ALink data-testid="welcome-paynow-link" onClick={() => setPayChosen(true)}>
              Prefer to pay now and skip the trial?
            </ALink>
          </div>
        )}

        {payChosen && (
          <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-4">
            <SubscribeBox onSubscribed={() => setPaid(true)} />
          </div>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={complete} className="space-y-4">
      <div className="flex items-baseline justify-between">
        <h2 className="text-xl font-semibold tracking-tight text-zinc-900">Set your PIN</h2>
        <StepTag step={3} total={3} />
      </div>
      <p className="text-sm text-zinc-500">You'll log in with your mobile number and this PIN from now on.</p>
      <AError>{error}</AError>
      <AField label="Choose a PIN (4–6 digits)">
        <AInput data-testid="signup-pin-input" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))} type="password" inputMode="numeric" placeholder="••••" />
      </AField>
      <AField label="Type it again">
        <AInput data-testid="signup-pin-confirm-input" value={pin2} onChange={(e) => setPin2(e.target.value.replace(/\D/g, "").slice(0, 6))} type="password" inputMode="numeric" placeholder="••••" />
      </AField>
      <ABtn data-testid="signup-complete-button" type="submit" busy={busy} disabled={pin.length < 4}>Create my account</ABtn>
      {busy && (
        <p className="text-center text-xs text-zinc-500">Setting up a board that fits your business…</p>
      )}
    </form>
  );
};
